import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { BOOKS } from "../data/content";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

export const usePreorder = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  return async () => {
    if (!user) { toast.error("Please log in to pre-order."); nav("/login"); return; }
    try {
      const { data } = await api.post("/checkout/session", { program_id: "gamechanger-preorder", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout. Try again.");
    }
  };
};

export const BookCoverCard = ({ book, testid }) => {
  const preorder = usePreorder();
  return (
    <div data-testid={testid}
      className="group bg-white border border-[#21302A]/10 flex flex-col hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full">
      <div className="relative overflow-hidden bg-[#132619]">
        <img src={book.cover} alt={`${book.title} cover`}
          className="w-full aspect-[4/5] object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        {book.preorder && (
          <span className="absolute top-4 right-4 bg-[#C0953A] text-[#132619] text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 font-semibold">
            Releases {book.releaseLabel}
          </span>
        )}
      </div>
      <div className="p-6 flex flex-col gap-2 flex-1">
        <span className="eyebrow">{book.tag}</span>
        <h3 className="font-serif-d text-2xl text-[#1E3A2B] leading-snug">{book.title}</h3>
        <p className="text-[#21302A]/65 text-sm leading-relaxed flex-1">{book.desc}</p>
        {book.link && (
          <a href={book.link} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-buy-link`}
            className="mt-4 inline-flex items-center justify-center gap-2 bg-[#1E3A2B] text-[#FAF4E9] hover:bg-[#2E5240] transition-colors px-6 py-3.5 uppercase tracking-widest text-xs font-semibold">
            <ShoppingBag size={14} /> {book.linkLabel || "Get it on Amazon"}
          </a>
        )}
        {book.preorder && (
          <button onClick={preorder} data-testid={`${testid}-preorder-btn`}
            className="mt-4 inline-flex items-center justify-center gap-2 bg-[#C0953A] text-[#132619] hover:bg-[#DCBB6C] transition-colors px-6 py-3.5 uppercase tracking-widest text-xs font-semibold">
            <Sparkles size={14} /> Pre-Order · ${book.price}
          </button>
        )}
      </div>
    </div>
  );
};

export const BooksShowcase = () => {
  const featured = BOOKS.filter((b) => b.cover);
  return (
    <section className="bg-white px-6 py-28 border-t border-[#21302A]/10" data-testid="books-showcase">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="eyebrow">From the Bookshelf</p>
          <h2 className="font-serif-d text-4xl md:text-5xl text-[#1E3A2B] tracking-tight mt-3">
            Words that <span className="italic-emph text-[#C0953A]">reset</span> you.
          </h2>
          <p className="text-[#21302A]/65 mt-4 max-w-lg">Real covers, real stories — written to rebuild the reader from the inside out.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
          {featured.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.12}>
              <BookCoverCard book={b} testid={`featured-book-${i}`} />
            </Reveal>
          ))}
        </div>
        <Reveal className="text-center mt-12">
          <Link to="/read" data-testid="books-see-all"
            className="inline-block border border-[#1E3A2B] text-[#1E3A2B] hover:bg-[#1E3A2B] hover:text-[#FAF4E9] transition-colors px-8 py-4 uppercase tracking-widest text-xs font-medium">
            Explore the Full Library →
          </Link>
        </Reveal>
      </div>
    </section>
  );
};
